import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { GenericView, SearchInput } from '../../components/common/table';
import { useAuth } from '../../contexts/AuthProvider';
const LvvScheme = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canLvvScheme = (key) => can(key, 'lvvScheme');

    // Pagination
    const pagination = usePagination(10);

    // Search
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
        setSearch(value);
        setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        pagination.setPage(0);
    };

    // Selection
    const {
        selectedIds,
        setSelectedIds,
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    // Data and form state
    const [schemes, setSchemes] = useState([]);
    const [standards, setStandards] = useState([]);
    const [formData, setFormData] = useState({ name: '', categoryId: '' });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');

    // Fetch schemes (match working copy)
    const fetchLvvSchemes = async (searchValue = search) => {
        try {
            setIsLoading(true);
            const response = await api.get(`/md/lvv-scheme`, {
                params: {
                    page: pagination.page,
                    size: pagination.size,
                    search: searchValue
                }
            });
            setSchemes(response.data.content);
            pagination.setTotalPages(response.data.totalPages);
            pagination.setCurrentPageItems(response.data.content.length);
            pagination.setTotal(response.data.totalElements);
        } catch (error) {
            console.error('Error fetching LVV schemes:', error);
            let errorMessage = 'Failed to fetch LVV schemes.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view LVV schemes.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server error while fetching LVV schemes.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching LVV schemes.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setSchemes([]);
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchStandards = async () => {
        try {
            const response = await api.get('/md/standards');
            setStandards(response.data);
        } catch (error) {
            console.error('Error fetching standards:', error);
            let errorMessage = 'Failed to fetch standards.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view standards.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server error while fetching standards.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching standards.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setStandards([]);
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    useEffect(() => {
        fetchLvvSchemes(appliedSearchTerm);
        fetchStandards();
    }, [pagination.page, pagination.size, appliedSearchTerm]);

    // Open modal for create
    const handleCreate = () => {
    if (!canLvvScheme('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({ name: '', standardId: '' });
        setEditingId(null);
        setErrors({});
        // Use the correct modal id and check for bootstrap.Modal existence
        const modalEl = document.getElementById('modal-lvv-scheme');
        if (window.bootstrap && window.bootstrap.Modal && modalEl) {
            const modal = new window.bootstrap.Modal(modalEl);
            modal.show();
        }
    }

    const handleEdit = (item) => {
    if (!canLvvScheme('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setFormData({
            name: item.scheme,
            standardId: item.standard?.id ? String(item.standard.id) : ''
        });
        setEditingId(item.id);
        setErrors({});
        setTimeout(() => {
            const modalEl = document.getElementById('modal-lvv-scheme');
            if (window.bootstrap && window.bootstrap.Modal && modalEl) {
                const modal = new window.bootstrap.Modal(modalEl);
                modal.show();
            }
        }, 100);
    };

    // Real-time validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        let error = null;
        if (name === 'name') {
            error = validateField('name', value, { required: true });
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // TomSelect change handler
    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        let error = null;
        if (name === 'standards') {
            error = value.length === 0 ? 'At least one Standard is required' : null;
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // Unified handleSubmit for both create and edit
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validate name and standardId fields only
        let error = validateField('name', formData.name, { required: true });
        let standardError = validateField('standardId', formData.standardId, { required: true });
        setErrors({ name: error, standardId: standardError });
        if (error || standardError) return;
        try {
            if (editingId) {
                await api.put(`/md/lvv-scheme/${editingId}`, { id: editingId, scheme: formData.name, standardId: formData.standardId });
                setSuccessMessage('LVV Scheme has been successfully updated.');
            } else {
                await api.post('/md/lvv-scheme', { scheme: formData.name, standardId: formData.standardId });
                setSuccessMessage('LVV Scheme has been successfully added.');
            }
            setShowSuccessModal(true);
            setEditingId(null);
            setFormData({ name: '', standardId: '' });
            setErrors({});
            await fetchLvvSchemes();
            const modalEl = document.getElementById('modal-lvv-scheme');
            const bootstrapModal = window.bootstrap?.Modal.getInstance(modalEl);
            if (bootstrapModal) bootstrapModal.hide();
        } catch (error) {
            console.error('Error saving LVV scheme:', error);
            let errorMessage = 'Failed to save LVV scheme.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 409:
                        errorMessage = 'An LVV scheme with this name already exists.';
                        technicalDetails = `Duplicate entry for name: "${formData.name}"`;
                        break;
                    case 400:
                        errorMessage = 'Invalid data provided.';
                        technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to perform this action.';
                        technicalDetails = 'Required role or permission is missing for this operation';
                        break;
                    case 404:
                        errorMessage = 'LVV scheme not found.';
                        technicalDetails = 'The requested item may have been deleted or does not exist';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while processing the request.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while saving the LVV scheme.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };


    // Edit selected (only works for single selection)
    const editSelected = () => {
        if (Array.isArray(selectedIds) && selectedIds.length !== 1) {
            setSuccessMessage('Please select exactly one NACE Code to edit');
            setShowSuccessModal(true);
            return;
        }
        const selectedId = Array.isArray(selectedIds) ? selectedIds[0] : null;
        const selectedItem = Array.isArray(schemes) && schemes.find(item => item.id === selectedId);
        if (selectedItem) {
            handleEdit(selectedItem);
        }
    };

    // Delete selected (bulk)
    const deleteSelected = () => {
    if (!canLvvScheme('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (Array.isArray(selectedIds) && selectedIds.length === 0) {
            setFailMessage('Please select at least one LVV scheme to delete.\n\nTechnical Details: No items selected for deletion');
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                await Promise.all(selectedIds.map(id => api.delete(`/md/lvv-scheme/${id}`)));
                setSelectedIds([]);
                fetchLvvSchemes();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected LVV schemes deleted successfully');
                setShowSuccessModal(true);
            } catch (error) {
                console.error('Error deleting LVV schemes:', error);
                let errorMessage = 'Failed to delete LVV schemes.';
                let technicalDetails = '';

                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            errorMessage = 'You do not have permission to delete these items.';
                            technicalDetails = 'Required role or permission is missing for deletion';
                            break;
                        case 400:
                            errorMessage = 'Invalid delete request.';
                            technicalDetails = error.response.data.message || 'The server rejected the deletion request';
                            break;
                        case 404:
                            errorMessage = 'One or more items not found.';
                            technicalDetails = 'Some items may have been already deleted';
                            break;
                        case 405:
                            errorMessage = 'Delete operation not allowed.';
                            technicalDetails = 'The server does not support the DELETE method for this endpoint';
                            break;
                        case 500:
                            errorMessage = 'Server error while deleting items.';
                            technicalDetails = error.response.data.message || 'Internal server error during deletion';
                            break;
                        default:
                            errorMessage = 'An error occurred while deleting the items.';
                            technicalDetails = `Server responded with status: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Unable to connect to the server.';
                    technicalDetails = 'Network error or server is not responding';
                } else {
                    errorMessage = 'An unexpected error occurred during deletion.';
                    technicalDetails = error.message;
                }

                setShowDeleteConfirmation(false);
                setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    // Modal cleanup
    useEffect(() => {
        const modal = document.getElementById('modal-lvv-scheme');
        modal?.addEventListener('hidden.bs.modal', () => {
            setFormData({ name: '', standardId: '' });
            setEditingId(null);
        });
        return () => {
            modal?.removeEventListener('hidden.bs.modal', () => { });
        };
    }, []);

    const columns = [
        { key: 'scheme', label: 'Scheme', sortable: true },
        { key: 'standard', label: 'Standard', sortable: true, render: (value) => value?.name || '-' },
        { key: 'cuser', label: 'Created By', sortable: false, render: (value, row) => row.cuser?.username || 'Unknown' },
        { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleDateString() : '-' },
    ];

    return (
        <>
                {/* Page header */}
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <h2 className="page-title">
                                    LVV Scheme
                                </h2>
                            </div>
                            {/* Page title actions */}
                            <div className="col-auto ms-auto d-print-none">
                                <div className="btn-list">
                                    <a href="#" className="btn btn-primary d-none d-sm-inline-block"
                                        onClick={handleCreate}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                        Create
                                    </a>
                                    <a href="#" className="btn btn-primary d-sm-none btn-icon" data-bs-toggle="modal"
                                        onClick={handleCreate}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Page body */}
                <div className="page-body">
                    <div className="container-xl">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <GenericView
                                        data={schemes}
                                        columns={columns}
                                        isLoading={isLoading || capabilitiesLoading}
                                        emptyStateMessage="No LVV Schemes found"
                                        checkboxSelection={true}
                                        selectedRows={selectedIds}
                                        onSelectRow={(itemId, isSelected) => {
                                            if (isSelected) {
                                                setSelectedIds([...selectedIds, itemId]);
                                            } else {
                                                setSelectedIds(selectedIds.filter(id => id !== itemId));
                                            }
                                        }}
                                        onSelectAllRows={(itemIds) => {
                                            if (Array.isArray(itemIds) && itemIds.length > 0) {
                                                setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                            } else {
                                                setSelectedIds([]);
                                            }
                                        }}
                                        onEdit={canLvvScheme('edit') ? (item) => {
                                            setSelectedIds([item.id]);
                                            handleEdit(item);
                                        } : undefined}
                                        onDelete={canLvvScheme('delete') ? (item) => {
                                            setSelectedIds([item.id]);
                                            deleteSelected();
                                        } : undefined}
                                        searchComponent={
                                            <div className="d-flex gap-2">
                                                <SearchInput
                                                    value={searchTerm}
                                                    onChange={handleSearchInputChange}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleSearchSubmit();
                                                        }
                                                    }}
                                                    placeholder="Search..."
                                                    uniqueId="lvv-scheme-search"
                                                />
                                                <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                                                    Search
                                                </button>
                                            </div>
                                        }
                                        actionButtons={
                                            <div className="d-flex gap-2">
                                                {(canLvvScheme('edit') || canLvvScheme('delete')) && (
                                                    <div ref={actionsContainerRef} className="dropdown">
                                                        <a
                                                            ref={actionsToggleRef}
                                                            href="#"
                                                            className="btn dropdown-toggle"
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded={isActionsOpen}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                const inst = actionsDropdownInstanceRef.current;
                                                                if (inst?.toggle) inst.toggle();
                                                                else setIsActionsOpen(prev => !prev);
                                                            }}
                                                        >Actions</a>
                                                        <div className="dropdown-menu">
                                                            {canLvvScheme('edit') && (
                                                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                                                    style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                                                    Edit
                                                                </a>
                                                            )}
                                                            {canLvvScheme('delete') && (
                                                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                                                    style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                                    Delete
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        }
                                        pagination={{
                                            currentPage: pagination.page + 1,
                                            totalRows: pagination.total,
                                            rowsPerPage: pagination.size,
                                            onPageChange: (page) => pagination.setPage(page - 1),
                                            onRowsPerPageChange: (size) => {
                                                pagination.setSize(size);
                                                pagination.setPage(0);
                                            }
                                        }}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        uniqueId="lvv-scheme-table"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Create and edit Lvv Scheme Modal */}
                <div className="modal modal-blur fade" id="modal-lvv-scheme" tabIndex="-1" role="dialog" aria-hidden="true">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editingId ? 'Edit Lvv Scheme' : 'Create Lvv Scheme'}</h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-lg-12">
                                            <div className="mb-3">
                                                <TomSelect
                                                    key={editingId || 'create'}
                                                    name="standardId"
                                                    value={formData.standardId ? String(formData.standardId) : ''}
                                                    onChange={(name, value) => {
                                                        handleSelectChange(name, value);
                                                    }}
                                                    options={standards.filter(s => s && s.id && s.name).map(standard => ({ id: String(standard.id), name: standard.name }))}
                                                    placeholder="Select Standard"
                                                    label="Standard"
                                                    required={true}
                                                    error={errors.standardId}
                                                    valueField="id"
                                                    labelField="name"
                                                    searchField="name"
                                                    multiple={false}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-12">
                                            <div className="mb-3">
                                                <label className="form-label required">Scheme Name</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter scheme name"
                                                />
                                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <a href="#" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">Cancel</a>
                                    <button type="submit" className="btn btn-primary ms-auto" id="createBtn">
                                        {editingId ? 'Update' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            
            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => { })}
                title="Delete Confirmation"
                message={Array.isArray(selectedIds) && selectedIds.length > 0
                    ? "Are you sure you want to delete the selected lvv scheme? This action cannot be undone."
                    : "Are you sure you want to delete this lvv scheme? This action cannot be undone."}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Success"
                message={successMessage}
                buttonText="OK"
            />
            {/* Fail Modal */}
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Error"
                message={failMessage}
                buttonText="OK"
            />
        </>
    );
};

export default LvvScheme;